import { useState, useEffect, useRef } from 'react';

import { IPoint, ICallbackFunction } from '../global/interfaces';
import { SHAPES } from '../global/constants/general';

const useCanvasSketch = () => {
  const canvasSketchRef = useRef<HTMLCanvasElement>(null);
  const sendPayloadRef = useRef<ICallbackFunction | null>(null);

  const setSendPayload = (callback: ICallbackFunction | null) => {
    sendPayloadRef.current = callback;
  };

  const [isStrokeListUpdated, setIsStrokeListUpdated] =
    useState<boolean>(false);
  const [color, setColor] = useState<string>('#D0021B');
  const [strokeList, setStrokeList] = useState<any[]>([]);
  const [pen, setPen] = useState<number>(1);
  const [shape, setShape] = useState<number>(0);

  const { DRAW, SQUARE, CIRCLE, ARROW } = SHAPES;
  const [startPoint, setStartPoint] = useState<IPoint | null>(null);
  const [currentStroke, setCurrentStroke] = useState<any>({});

  const _setStrokeList = (strokes: any) => {
    console.log(':::_setStrokeList:', strokes);
    setIsStrokeListUpdated(true);
    setStrokeList(strokes);
  };

  useEffect(() => {
    console.log('++++useEffect updated strokeList...');

    const canvasSketch = canvasSketchRef?.current;
    draw(
      canvasSketch,
      !currentStroke ? strokeList : [...strokeList, currentStroke],
      true,
    );
  }, [strokeList, currentStroke]);

  const clearCanvas = () => {
    const canvasSketch = canvasSketchRef?.current;
    const canvasCtx = canvasSketch?.getContext('2d');
    if (!canvasCtx || !canvasSketch) return;

    console.log('clearCanvas...');
    canvasCtx.clearRect(0, 0, canvasSketch.width, canvasSketch.height);
    _setStrokeList([]);
    if (sendPayloadRef?.current !== null) {
      sendPayloadRef.current({
        type: 'canvas-sketch-clear',
      });
    }
  };

  const draw = (canvasSketch: any, strokes: any, clear: boolean = false) => {
    const canvasCtx = canvasSketch?.getContext('2d');
    if (
      !canvasSketch ||
      !canvasSketch.width ||
      !canvasSketch.height ||
      !canvasCtx
    )
      return;

    if (clear)
      canvasCtx.clearRect(0, 0, canvasSketch.width, canvasSketch.height);

    strokes?.forEach((stroke: { [key: string]: any }) => {
      canvasCtx.beginPath();
      canvasCtx.lineWidth = stroke.pen;
      canvasCtx.strokeStyle = stroke.color;

      switch (stroke.shape) {
        case DRAW:
          canvasCtx.moveTo(
            stroke.points[0][0] * canvasSketch.width,
            stroke.points[0][1] * canvasSketch.width,
          );
          for (let i = 1; i < stroke.points.length; i++)
            canvasCtx.lineTo(
              stroke.points[i][0] * canvasSketch.width,
              stroke.points[i][1] * canvasSketch.width,
            );
          break;

        case SQUARE:
          canvasCtx.rect(
            stroke.x * canvasSketch.width,
            stroke.y * canvasSketch.width,
            stroke.w * canvasSketch.width,
            stroke.h * canvasSketch.width,
          );
          break;

        case CIRCLE:
          canvasCtx.arc(
            stroke.x * canvasSketch.width,
            stroke.y * canvasSketch.width,
            stroke.r * canvasSketch.width,
            0,
            2 * Math.PI,
          );
          break;

        case ARROW:
          drawArrow(
            canvasCtx,
            stroke.fx * canvasSketch.width,
            stroke.fy * canvasSketch.width,
            stroke.tx * canvasSketch.width,
            stroke.ty * canvasSketch.width,
          );
          break;
      }

      canvasCtx.stroke();
    });
  };

  const drawArrow = (
    canvasCtx: any,
    fromX: any,
    fromY: any,
    toX: any,
    toY: any,
  ) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    canvasCtx.beginPath();
    canvasCtx.moveTo(fromX, fromY);
    canvasCtx.lineTo(toX, toY);

    canvasCtx.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 6),
      toY - headLength * Math.sin(angle - Math.PI / 6),
    );
    canvasCtx.moveTo(toX, toY);
    canvasCtx.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 6),
      toY - headLength * Math.sin(angle + Math.PI / 6),
    );
  };

  const undoChanges = () => {
    _setStrokeList(strokeList.slice(0, -1));
    if (sendPayloadRef?.current !== null) {
      sendPayloadRef.current({
        type: 'canvas-sketch-undo',
      });
    }
  };

  const mouseDown = (event: any) => {
    const canvasSketch = canvasSketchRef?.current;
    if (!canvasSketch?.width || !canvasSketch?.height) return;
    const { offsetX, offsetY } = event.nativeEvent;

    console.log(
      'mouse down: ' +
        +(offsetX / canvasSketch.width).toFixed(4) +
        ', ' +
        +(offsetY / canvasSketch.width).toFixed(4),
    );

    setStartPoint({
      x: +(offsetX / canvasSketch.width).toFixed(4),
      y: +(offsetY / canvasSketch.width).toFixed(4),
    });
  };

  const mouseMove = (event: any) => {
    const canvasSketch = canvasSketchRef?.current;
    if (!startPoint || !canvasSketch?.width || !canvasSketch?.height) return;
    const { offsetX, offsetY } = event.nativeEvent;
    const [fOffsetX, fOffsetY] = [
      +(offsetX / canvasSketch.width).toFixed(4),
      +(offsetY / canvasSketch.width).toFixed(4),
    ];

    const attributes = { shape, color, pen };
    let stroke =
      (shape === DRAW && {
        ...attributes,
        points: !currentStroke?.points
          ? [
              [startPoint.x, startPoint.y],
              [fOffsetX, fOffsetY],
            ]
          : [...currentStroke.points, [fOffsetX, fOffsetY]],
      }) ||
      (shape === SQUARE && {
        ...attributes,
        x: Math.min(startPoint.x, fOffsetX),
        y: Math.min(startPoint.y, fOffsetY),
        w: Math.abs(startPoint.x - fOffsetX),
        h: Math.abs(startPoint.y - fOffsetY),
      }) ||
      (shape === CIRCLE && {
        ...attributes,
        x: startPoint.x,
        y: startPoint.y,
        r: Math.sqrt(
          Math.pow(fOffsetX - startPoint.x, 2) +
            Math.pow(fOffsetY - startPoint.y, 2),
        ),
      }) ||
      (shape === ARROW && {
        ...attributes,
        fx: startPoint.x,
        fy: startPoint.y,
        tx: fOffsetX,
        ty: fOffsetY,
      });

    setCurrentStroke(stroke);
    return;
  };

  const mouseUp = () => {
    if (currentStroke && Object.keys(currentStroke).length > 0) {
      _setStrokeList([...strokeList, currentStroke]);

      console.log(
        ':::::mouseup.... sendPayloadRef?.current:',
        sendPayloadRef?.current,
      );

      console.log(':::::sendPayloadRef?.current:', sendPayloadRef?.current);

      if (sendPayloadRef?.current !== null) {
        sendPayloadRef.current({
          type: 'canvas-sketch-stroke',
          stroke: currentStroke,
        });
      }
    }
    mouseOut();
  };

  const mouseOut = () => {
    setStartPoint(null);
    setCurrentStroke(null);
  };

  //maybe move to SessionContext
  const handleDeleteImage = async (handleCloseScreenshotEditor: any) => {
    try {
      /*
      const { session_images, session_transcripts } =
        await deleteImage.mutateAsync({
          bookingId: selectedBooking?.booking_id,
          imageId: selectedImage?.id,
        })

//      setSession({
//        ...booking,
//        session_images,
//        session_transcripts,
//      })

      //sendMessage({ 'updated-images': true })
      handleCloseScreenshotEditor();
      */
    } catch (error) {
      console.log(error);
    }
  };

  return {
    canvasSketchRef,
    setSendPayload,

    color,
    setColor,

    strokeList,
    setStrokeList,

    isStrokeListUpdated,
    setIsStrokeListUpdated,

    pen,
    setPen,
    shape,
    setShape,

    undoChanges,

    mouseDown,
    mouseMove,
    mouseUp,
    mouseOut,

    handleDeleteImage,
    clearCanvas,
    draw,
  };
};

export default useCanvasSketch;
